import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Building2,
  CheckCircle2,
  Receipt,
  Smartphone,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableCard } from "@/components/shared/TableCard";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getPlatformMetricsOverview,
  type PlatformMetricsOverview,
} from "@/services/platformMetricsService";
import { TaxCalendarTrackerCard } from "@/pages/platform-metrics/TaxCalendarTrackerCard";
import { SupportMetricsCard } from "@/pages/platform-metrics/SupportMetricsCard";

const PLAN_LABEL: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  ultra: "Ultra",
};

const DGI_STATUS_LABEL: Record<string, string> = {
  confirmed: "Confirmadas",
  rejected: "Rechazadas",
  pending: "Pendientes DGI",
  sent: "Enviadas a DGI",
};

/**
 * Root-only platform analytics. Aggregates across tenants — onboardings,
 * invoice throughput, top tenants by activity this month, DGI weekly
 * breakdown. Anchored on Uruguay calendar boundaries (see backend), so
 * "today" / "esta semana" / "este mes" reflect UY local periods.
 */
export function PlatformMetricsPage(): JSX.Element {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const { data, isPending, isError } = useQuery({
    queryKey: ["platform", "metrics", "overview"],
    queryFn: getPlatformMetricsOverview,
    // Snapshot is heavy; cache for a couple of minutes.
    staleTime: 2 * 60 * 1000,
    // If the endpoint isn't reachable (e.g. backend not redeployed),
    // surface the error fast instead of spinning through React Query's
    // default 3-retry exponential backoff (~10s of spinner).
    retry: 1,
  });

  // Render content as soon as we have data. React Query v5's isPending
  // covers the "no data yet" case; isError surfaces failed fetches.
  // Driving off `data` directly (not isLoading) avoids edge cases where
  // the loading flag lingers after the response lands.
  if (data == null) {
    if (isError) {
      return (
        <div className="fx-content space-y-4">
          <PageHeader title="Métricas de la plataforma" />
          <p className="text-sm text-muted-foreground">
            No pudimos cargar las métricas. Intentá de nuevo.
          </p>
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="fx-content flex min-h-[60vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      );
    }
  }
  if (!data) {
    return (
      <div className="fx-content space-y-4">
        <PageHeader title="Métricas de la plataforma" />
        <p className="text-sm text-muted-foreground">
          No pudimos cargar las métricas. Intentá de nuevo.
        </p>
      </div>
    );
  }

  return (
    <div className="fx-content space-y-6">
      <PageHeader
        title="Métricas de la plataforma"
        description={`Snapshot · ${formatDisplayDate(data.generatedAt, {
          locale: i18n.language,
          includeTime: true,
        })}`}
      />

      {/* KPI cards — invoices throughput + tenants */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={<Receipt className="h-4 w-4" aria-hidden />}
          label="Facturas hoy"
          value={data.invoices.today}
        />
        <KpiCard
          icon={<TrendingUp className="h-4 w-4" aria-hidden />}
          label="Facturas esta semana"
          value={data.invoices.thisWeek}
        />
        <KpiCard
          icon={<BarChart3 className="h-4 w-4" aria-hidden />}
          label="Facturas este mes"
          value={data.invoices.thisMonth}
        />
        <KpiCard
          icon={<Sparkles className="h-4 w-4" aria-hidden />}
          label="Tenants nuevos esta semana"
          value={data.tenants.onboardedThisWeek}
          accent
        />
      </div>

      {/* Tenants snapshot */}
      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard
          icon={<Building2 className="h-4 w-4" aria-hidden />}
          label="Tenants activos"
          value={data.tenants.activelyOnboarded}
          subtitle={`${data.tenants.total} totales`}
        />
        <KpiCard
          icon={<Users className="h-4 w-4" aria-hidden />}
          label="En onboarding"
          value={data.tenants.onboardingPending}
        />
        <KpiCard
          icon={<CheckCircle2 className="h-4 w-4" aria-hidden />}
          label="DGI confirmadas (semana)"
          value={
            data.dgi.weekByDgiStatus.find((x) => x.dgiStatus === "confirmed")
              ?.count ?? 0
          }
        />
      </div>

      {/* Mobile usage — populated once the X-Client-stamping deploy is
          live; pre-deploy backfills aren't possible, so historical
          numbers stay at zero. */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight">
          Uso del app móvil
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <KpiCard
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
            label="DAU"
            value={data.mobile.dau}
            subtitle="Usuarios activos hoy"
            accent
          />
          <KpiCard
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
            label="WAU"
            value={data.mobile.wau}
            subtitle="Últimos 7 días"
          />
          <KpiCard
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
            label="MAU"
            value={data.mobile.mau}
            subtitle="Últimos 30 días"
          />
          <KpiCard
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
            label="Instalaciones activas"
            value={data.mobile.activeInstalls}
            subtitle="Tokens de refresh vigentes"
          />
          <KpiCard
            icon={<Smartphone className="h-4 w-4" aria-hidden />}
            label="Usuarios alguna vez"
            value={data.mobile.everSignedIn}
            subtitle="Total histórico"
          />
        </div>
      </section>

      {/* Top tenants by month */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight">
          Top tenants por facturación · este mes
        </h2>
        {data.invoices.topTenantsByMonth.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aún no hay facturas emitidas este mes.
          </p>
        ) : (
          <TableCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead className="text-right">Facturas (mes)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invoices.topTenantsByMonth.map((row) => (
                  <TableRow
                    key={row.tenantId}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => navigate(`/tenants/${row.tenantId}`)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {row.tenantName?.trim() ||
                            row.legalName?.trim() ||
                            row.rut}
                        </span>
                        {row.legalName?.trim() &&
                        row.tenantName?.trim() &&
                        row.legalName.trim() !== row.tenantName.trim() ? (
                          <span className="text-xs text-muted-foreground">
                            {row.legalName.trim()}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {row.rut}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {row.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>
        )}
      </section>

      {/* Recent onboardings */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight">
          Onboardings recientes
        </h2>
        {data.tenants.recentOnboardings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No hay onboardings completados todavía.
          </p>
        ) : (
          <TableCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Onboarding completado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tenants.recentOnboardings.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => navigate(`/tenants/${row.id}`)}
                  >
                    <TableCell>
                      <span className="font-medium">
                        {row.name?.trim() ||
                          row.legalName?.trim() ||
                          row.rut}
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {row.rut}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.planId ? PLAN_LABEL[row.planId] ?? row.planId : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDisplayDate(row.onboardingCompletedAt, {
                        locale: i18n.language,
                        includeTime: true,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>
        )}
      </section>

      {/* Plan + DGI breakdown side by side */}
      <section className="grid gap-3 lg:grid-cols-2">
        <BreakdownCard
          title="Tenants por plan"
          rows={planRows(data)}
        />
        <BreakdownCard
          title="Facturas esta semana · estado DGI"
          rows={dgiRows(data)}
        />
      </section>

      <SupportMetricsCard />
      <TaxCalendarTrackerCard />
    </div>
  );
}

/* ---------- pieces ---------- */

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  subtitle?: string;
  accent?: boolean;
}

function KpiCard({
  icon,
  label,
  value,
  subtitle,
  accent,
}: KpiCardProps): JSX.Element {
  return (
    <div
      className={
        "rounded-[var(--fx-r-md)] border border-[var(--fx-ink-100)] bg-white px-5 py-4 dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface)] " +
        (accent
          ? "shadow-[0_1px_0_rgba(45,97,73,0.08),0_10px_28px_-18px_rgba(45,97,73,0.30)]"
          : "shadow-[0_1px_0_rgba(22,21,20,0.03)]")
      }
    >
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]">
        <span
          className={
            "inline-flex h-6 w-6 items-center justify-center rounded-md " +
            (accent
              ? "bg-[var(--fx-accent-100)] text-[var(--fx-accent-700)] dark:bg-[rgba(111,165,133,0.16)] dark:text-[var(--fx-accent-300)]"
              : "bg-[var(--fx-ink-100)] text-[var(--fx-ink-700)] dark:bg-[rgba(244,239,230,0.08)] dark:text-[var(--dk-text-soft)]")
          }
        >
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
        {value}
      </div>
      {subtitle ? (
        <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>
      ) : null}
    </div>
  );
}

interface BreakdownCardProps {
  title: string;
  rows: Array<{ label: string; count: number }>;
}

function BreakdownCard({ title, rows }: BreakdownCardProps): JSX.Element {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div className="rounded-[var(--fx-r-md)] border border-[var(--fx-ink-100)] bg-white p-5 dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface)]">
      <h3 className="text-sm font-semibold tracking-tight text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
        {title}
      </h3>
      <ol className="mt-3 space-y-2">
        {rows.length === 0 ? (
          <li className="text-sm text-muted-foreground">Sin datos.</li>
        ) : (
          rows.map((r) => {
            const pct = total === 0 ? 0 : Math.round((r.count / total) * 100);
            return (
              <li key={r.label} className="text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]">
                    {r.label}
                  </span>
                  <span className="font-medium tabular-nums">
                    {r.count}{" "}
                    <span className="text-xs text-muted-foreground">
                      ({pct}%)
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-[var(--fx-ink-100)] dark:bg-[rgba(244,239,230,0.08)]">
                  <div
                    className="h-full rounded-full bg-[var(--fx-accent-500)]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })
        )}
      </ol>
    </div>
  );
}

/* ---------- helpers ---------- */

function planRows(
  data: PlatformMetricsOverview,
): Array<{ label: string; count: number }> {
  return data.tenants.byPlan.map((r) => ({
    label: r.planId ? PLAN_LABEL[r.planId] ?? r.planId : "Sin plan",
    count: r.count,
  }));
}

function dgiRows(
  data: PlatformMetricsOverview,
): Array<{ label: string; count: number }> {
  return data.dgi.weekByDgiStatus.map((r) => ({
    label: DGI_STATUS_LABEL[r.dgiStatus] ?? r.dgiStatus,
    count: r.count,
  }));
}
